import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon, MapPinIcon, SearchIcon, ShieldCheckIcon, BuildingIcon, ClipboardCheckIcon, ImageIcon, ChevronDownIcon, PhoneIcon, ClockIcon, StarIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
const faqs = [{
  question: 'What is a Google Business Profile?',
  answer: 'A Google Business Profile (formerly Google My Business) is a free listing that appears in Google Search and Google Maps when people search for your business or services in your area. It shows your business name, address, phone number, hours, reviews, and more.'
}, {
  question: "Why can't I just set it up myself?",
  answer: "You absolutely can. But many business owners run into issues with verification, choosing the right categories, setting service areas correctly, or getting the profile to actually show up in local searches. We handle the technical details so it's done right the first time."
}, {
  question: 'How long does the setup and verification take?',
  answer: 'Profile creation and optimization can be done within a few days. Verification timing depends on Google - it can take anywhere from a few days to a few weeks depending on the method Google requires for your business (postcard, phone, video, etc.).'
}, {
  question: "Does this guarantee I'll show up in the Google Map Pack?",
  answer: 'No. Map Pack rankings depend on many factors including reviews, proximity, relevance, and website authority. What this service does is make sure your profile is properly set up, verified, and optimized - which is the foundation for local visibility.'
}, {
  question: 'Is this a monthly service?',
  answer: "No, this is a one-time $397 service fee. Once your profile is set up and verified, it's yours. If you want ongoing optimization, review management, or local SEO, we offer those as separate monthly services."
}, {
  question: 'Does Google charge for a Business Profile?',
  answer: "No. Google Business Profiles are free. The $397 is Viridia's service fee for handling the setup, optimization, and verification process for you."
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-800 last:border-0" data-id="element-1833" data-mp="fse0hx3dyalb">
      <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-1834" data-mp="1rjs3egc89mn">
        <span className="text-lg font-semibold text-white" data-id="element-1835" data-mp="6r6d4a2equ60">{question}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0 ml-4 text-viridia" data-id="element-1836" data-mp="1w6mdp129n7ka">
          <ChevronDownIcon className="w-5 h-5" data-id="element-1837" data-mp="3ln820r342jxv" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-1838" data-mp="3goy8ekx1bred">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-1839" data-mp="3glsl9b7yuzla">
            <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-1840" data-mp="i5pvqo2zlkzk">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function GbpSetupPage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-1841" data-mp="2jxcsz7dh2c4h">
      <SEOHead title="Google Business Profile Setup & Verification | Phoenix | Viridia Analytics" description="Struggling to get your Google Business Profile verified? We handle the entire setup, optimization, and verification process so your Phoenix business shows up in Google Maps and local searches. $397 one-time." canonical="/google-business-profile-setup" keywords="Google Business Profile setup Phoenix, Google My Business verification, GBP setup service, Google Maps listing Phoenix, local business Google setup, Google Business Profile optimization Phoenix, marketing agency phoenix, HVAC, painters, landscapers, contractors, window installers" faq={faqs} data-id="element-1842" data-mp="2jy5r5jyd7tb0" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 overflow-hidden" data-id="element-1843" data-mp="s7k8pv4itarh">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-1844" data-mp="w9f6ipn8t60i">
          <div className="absolute top-[-10%] right-[-5%] w-[35%] h-[35%] rounded-full bg-blue-500/10 blur-[120px] animate-pulse-slow" data-id="element-1845" data-mp="114ckmazdq5k9"></div>
          <div className="absolute bottom-[-10%] left-[-5%] w-[30%] h-[30%] rounded-full bg-blue-500/10 blur-[100px] animate-pulse-slow" style={{
          animationDelay: '2s'
        }} data-id="element-1846" data-mp="8l5o7xp8n0e4"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1847" data-mp="3s6mq0oadh7ov">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1848" data-mp="3o5kqe6cjmtmd">
            <div className="max-w-2xl" data-id="element-1849" data-mp="3j6zps5tdq7xe">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-1850" data-mp="3ja5gfphnphv8">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-500 text-sm font-semibold tracking-wide mb-6" data-id="element-1851" data-mp="30hteh3ux0ilm">
                  LOCAL PRESENCE SERVICE
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-1852" data-mp="2qkon8pm7pwxy">
                  Google Business Profile{' '}
                  <span className="text-blue-500" data-id="element-1853" data-mp="2lv4ez20ny5f8">Launch & Verification</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-1854" data-mp="26ydd8m3iohmg">
                  If your Google Business Profile isn't set up or verified
                  correctly, you're invisible to local customers searching on
                  Google Maps. We handle the entire process so your business is
                  properly listed, visible, and ready to support reviews, calls,
                  and local trust.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-1855" data-mp="2pw676yw5xh7p">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300" data-id="element-1856" data-mp="2uuhg97emvnov">
                    Get Started - $397
                    <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1857" data-mp="1bvtaabhtyz58" />
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-1858" data-mp="16yjnz4pqn3lm">
                    View All Pricing
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Visual — Mock Google Maps Card */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="hidden lg:block" data-id="element-1859" data-mp="o18llb8t7u1y">
              <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-md mx-auto" data-id="element-1860" data-mp="29bz178fjsda3">
                <div className="flex items-center gap-4 mb-5 pb-4 border-b border-gray-100" data-id="element-1861" data-mp="6qwbwif77keo">
                  <div className="w-14 h-14 bg-viridia/10 rounded-full flex items-center justify-center" data-id="element-1862" data-mp="bpg1cst03ebs">
                    <MapPinIcon className="w-7 h-7 text-viridia" data-id="element-1863" data-mp="gm6h1f0w87wo" />
                  </div>
                  <div data-id="element-1864" data-mp="lf9dpw0yvxh3">
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-1865" data-mp="lfsl4iszr3gv">
                      Your Business Name
                    </h3>
                    <div className="flex items-center text-sm text-gray-600" data-id="element-1866" data-mp="uew3fkkc3eub">
                      <span className="text-yellow-500 flex items-center mr-2" data-id="element-1867" data-mp="ugz3kxf7aocr">
                        5.0 <StarIcon className="w-3 h-3 ml-1 fill-current" data-id="element-1868" data-mp="zf0ozp03mzre" />
                      </span>
                      (47 reviews) · Home Services
                    </div>
                  </div>
                </div>
                <div className="space-y-3 mb-5" data-id="element-1869" data-mp="364p08et5l00x">
                  <div className="flex items-center text-sm text-gray-600" data-id="element-1870" data-mp="365hx8o2b8b5v">
                    <MapPinIcon className="w-4 h-4 mr-3 text-gray-400 flex-shrink-0" data-id="element-1871" data-mp="1n9u8w9ncnbf9" />
                    Serving Phoenix, Scottsdale & surrounding areas
                  </div>
                  <div className="flex items-center text-sm text-gray-600" data-id="element-1872" data-mp="1eh0qi3clvvx6">
                    <PhoneIcon className="w-4 h-4 mr-3 text-gray-400 flex-shrink-0" data-id="element-1873" data-mp="1ef60wbqe3fmr" />
                    (623) 555-0123
                  </div>
                  <div className="flex items-center text-sm text-green-700 font-medium" data-id="element-1874" data-mp="268q1jgjk7hqz">
                    <ClockIcon className="w-4 h-4 mr-3 flex-shrink-0" data-id="element-1875" data-mp="226bkn1zgas2c" />
                    Open · Closes 6:00 PM
                  </div>
                </div>
                <div className="flex gap-2" data-id="element-1876" data-mp="1x9uw46ocaboq">
                  <div className="flex-1 bg-blue-50 text-blue-700 text-center py-2.5 rounded-full text-sm font-medium" data-id="element-1877" data-mp="1s9glqhz9mpnj">
                    Website
                  </div>
                  <div className="flex-1 bg-blue-50 text-blue-700 text-center py-2.5 rounded-full text-sm font-medium" data-id="element-1878" data-mp="gti6fwdhv7dv">
                    Directions
                  </div>
                  <div className="flex-1 bg-blue-50 text-blue-700 text-center py-2.5 rounded-full text-sm font-medium" data-id="element-1879" data-mp="1mlp238in0yv">
                    Call
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 text-center" data-id="element-1880" data-mp="bdg8kzudnmgm">
                  <span className="text-xs text-gray-400" data-id="element-1881" data-mp="6kn48d2tmg52">
                    ✓ Verified on Google
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-1882" data-mp="uapucnn6dc11">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1883" data-mp="u8n6oa9hvuzh">
          <ScrollReveal data-id="element-1884" data-mp="pcfx87hw41cs">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1885" data-mp="p9toi4j9jfsz">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1886" data-mp="2w35o7f2wncad">
                No Google Profile = No Local Visibility
              </h2>
              <p className="text-lg text-gray-400" data-id="element-1887" data-mp="16l8u5jp20i31">
                When someone in Phoenix searches "landscaper near me" or
                "plumber in Scottsdale," Google shows a map with 3 businesses.
                If your profile isn't set up and verified, you're not one of
                them.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1888" data-mp="11so0qja28701">
            {[{
            icon: <SearchIcon className="w-7 h-7" data-id="element-1889" data-mp="nwu00njfwny0" />,
            title: "You're Not Showing Up",
            desc: "Without a verified profile, your business doesn't appear in Google Maps or the local map pack - even if you have a great website."
          }, {
            icon: <ShieldCheckIcon className="w-7 h-7" data-id="element-1890" data-mp="ivwjefe2w1ym" />,
            title: 'Verification Is Confusing',
            desc: "Google's verification process can involve postcards, phone calls, or video walkthroughs. Many business owners get stuck or give up halfway through."
          }, {
            icon: <StarIcon className="w-7 h-7" data-id="element-1891" data-mp="2bq5nuzgqr8n9" />,
            title: 'Missing Trust Signals',
            desc: 'An incomplete or unverified profile can\'t collect reviews, display hours, or show up in "near me" searches - costing you jobs every day.'
          }].map((item, i) => <ScrollReveal key={i} delay={i * 0.1} data-id="element-1892" data-mp="2uj00hmqw6oob" data-mpi={i}>
                <div className="bg-viridia-black rounded-2xl p-8 border border-gray-800 h-full" data-id="element-1893" data-mp="2uigjc892g4bc">
                  <div className="w-14 h-14 rounded-xl bg-red-500/10 flex items-center justify-center text-red-400 mb-6" data-id="element-1894" data-mp="2pkf1fqndmb1g">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-1895" data-mp="2fuvea8smrv0s">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1896" data-mp="3db29cbxal0o0">{item.desc}</p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-24 bg-viridia-black" data-id="element-1897" data-mp="3gthxjjjqpynv">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1898" data-mp="3ks8mh3e1y9zw">
          <ScrollReveal data-id="element-1899" data-mp="3ps3c73m3ctjd">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1900" data-mp="3prk2292on9fw">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1901" data-mp="3pug2q55x0c81">
                What's Included
              </h2>
              <p className="text-lg text-gray-400" data-id="element-1902" data-mp="3leve0vbu9ac">
                Everything you need to get your Google Business Profile live,
                verified, and optimized - handled for you.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1903" data-mp="cdyv74ncujwr">
            {[{
            icon: <BuildingIcon className="w-6 h-6" data-id="element-1904" data-mp="mbd1ibfkdsbc" />,
            title: 'Create or Claim Your Profile',
            desc: 'We create a new profile or claim your existing one so you have full ownership and control.'
          }, {
            icon: <ShieldCheckIcon className="w-6 h-6" data-id="element-1905" data-mp="1o09zxjucjc4t" />,
            title: 'Verification Management',
            desc: "We guide or manage the entire verification process - whether it's postcard, phone, or video verification."
          }, {
            icon: <ClipboardCheckIcon className="w-6 h-6" data-id="element-1906" data-mp="1stma4oya3ge1" />,
            title: 'Correct Business Information',
            desc: 'Business name, address, phone number, and all core details entered accurately for NAP consistency.'
          }, {
            icon: <MapPinIcon className="w-6 h-6" data-id="element-1907" data-mp="1e3eyas2s0vmb" />,
            title: 'Service Area Configuration',
            desc: 'Set your service area and local coverage correctly so you show up in the right neighborhoods and cities.'
          }, {
            icon: <SearchIcon className="w-6 h-6" data-id="element-1908" data-mp="2pkoixhxn5tp7" />,
            title: 'Category & Description Setup',
            desc: 'We choose the right business categories and write a keyword-rich description to maximize your relevance.'
          }, {
            icon: <ClockIcon className="w-6 h-6" data-id="element-1909" data-mp="2y1fbikrm89zw" />,
            title: 'Hours, Website & Contact Info',
            desc: 'All your operating hours, website URL, phone number, and contact details properly configured.'
          }, {
            icon: <ImageIcon className="w-6 h-6" data-id="element-1910" data-mp="3bqzdhoddh385" />,
            title: 'Logo & Image Placeholders',
            desc: 'We upload your logo and branded images if assets are available, or set up placeholders you can update later.'
          }, {
            icon: <CheckCircleIcon className="w-6 h-6" data-id="element-1911" data-mp="37097c8x4gwce" />,
            title: 'Final Live Check',
            desc: 'We verify that your profile is live, properly configured, and appearing correctly in Google Search and Maps.'
          }].map((item, i) => <ScrollReveal key={i} delay={i * 0.05} data-id="element-1912" data-mp="3da9ugyx413i" data-mpi={i}>
                <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full group hover:border-viridia/30 transition-colors duration-300" data-id="element-1913" data-mp="d6d0l1q4mujd">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center text-viridia mb-4 group-hover:bg-viridia/20 transition-colors" data-id="element-1914" data-mp="46q3gs4fkaw4">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-1915" data-mp="3qe7rsy1z9ooc">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-1916" data-mp="3lmg2srygzr0b">
                    {item.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* PRICING BLOCK */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-1917" data-mp="37nzu4vh1rqfj">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1918" data-mp="32odgwm17ya6q">
          <ScrollReveal data-id="element-1919" data-mp="32ovlq71y5wq9">
            <div className="bg-viridia-black rounded-2xl p-8 md:p-12 border border-gray-800 text-center" data-id="element-1920" data-mp="32qpxovv8qei7">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-1921" data-mp="1n5fkzfaibt85">
                ONE-TIME SERVICE
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1922" data-mp="1w6dntu97lup5">
                $397
              </h2>
              <p className="text-gray-400 mb-2 text-lg" data-id="element-1923" data-mp="zh4sp0ivn1vh">One-time setup fee</p>
              <p className="text-gray-500 text-sm mb-8 max-w-lg mx-auto" data-id="element-1924" data-mp="14e4yrbfq84ml">
                This is Viridia's service fee for handling the setup,
                optimization, and verification process. Google does not charge
                for creating a Business Profile.
              </p>
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] hover:-translate-y-1" data-id="element-1925" data-mp="gpvc67aovcue">
                Book a Call to Get Started
                <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1926" data-mp="35c0mhrsx62ay" />
              </Link>
              <p className="text-gray-500 text-xs mt-6" data-id="element-1927" data-mp="3k8rlhzyolc01">
                Pairs well with our automation, ad management, and website
                services.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* WHO IS THIS FOR */}
      <section className="py-20 bg-viridia-black" data-id="element-1928" data-mp="3o7qovg4rl0pd">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1929" data-mp="3o6xn6tknx1e3">
          <ScrollReveal data-id="element-1930" data-mp="6rqxln64d3cu">
            <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-1931" data-mp="6u4qbpdbu3h8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1932" data-mp="1uig7ossp3cn">
                Who This Is For
              </h2>
              <p className="text-lg text-gray-400" data-id="element-1933" data-mp="fqvruhaneaj7">
                This service is built for local service business owners who need
                a solid starting point for their online presence.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-1934" data-mp="zdp7u95t5ift">
            {["New businesses that haven't set up a Google profile yet", 'Businesses that started the process but got stuck on verification', 'Owners who bought a business and need to claim the existing listing', "Contractors who've been relying on word-of-mouth and want to get found online", 'Businesses with an incomplete or outdated profile that needs a fresh start', 'Anyone who just wants it done right without the headache'].map((item, i) => <ScrollReveal key={i} delay={i * 0.05} data-id="element-1935" data-mp="6o0aq6w7y2jh" data-mpi={i}>
                <div className="flex items-start gap-3" data-id="element-1936" data-mp="3owqz7xxx1kkw">
                  <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-1937" data-mp="3tusudh8m9acq" />
                  <span className="text-gray-300" data-id="element-1938" data-mp="3twn5y4xzzq45">{item}</span>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-1939" data-mp="t9op1e66vkg">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1940" data-mp="3j324m7h6xcpr">
          <ScrollReveal data-id="element-1941" data-mp="3nziup77ya15h">
            <div className="text-center mb-16" data-id="element-1942" data-mp="3o0u69t7n0d75">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1943" data-mp="fkr0xu3zz0lz">
                Frequently Asked Questions
              </h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2} data-id="element-1944" data-mp="kleo24p3uxax">
            <div className="bg-viridia-black rounded-2xl p-6 md:p-8 border border-gray-800" data-id="element-1945" data-mp="5vh6i7lol5up">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-1946" data-mp="arxwl7cfxuaf" data-mpi={index} />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden" data-id="element-1947" data-mp="2nzgnvkn3stn4">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-1948" data-mp="2j0cdiqog0pv7"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-1949" data-mp="2wumwerdrdptc">
          <ScrollReveal data-id="element-1950" data-mp="2stbfda8yacwr">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1951" data-mp="251cq1mco2h0m">
              Ready to Show Up on Google Maps?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-1952" data-mp="203uqgmkjhe5a">
              Book a free call and we'll get your Google Business Profile set
              up, verified, and ready to bring in local customers.
            </p>
            <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-1953" data-mp="295kinum8wh44">
              Book Your Free Call
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1954" data-mp="2n3jui7n4p0rf" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}